import {
  HUB_FOLDERS_REQUEST, HUB_FOLDERS_SUCCESS, HUB_FOLDERS_FAIL,
  HUB_CREATE_FOLDER_REQUEST, HUB_CREATE_FOLDER_SUCCESS, HUB_CREATE_FOLDER_FAIL,
  HUB_RENAME_FOLDER_SUCCESS,
  HUB_DELETE_FOLDER_SUCCESS,
  HUB_FILES_REQUEST, HUB_FILES_SUCCESS, HUB_FILES_FAIL,
  HUB_UPLOAD_REQUEST, HUB_UPLOAD_SUCCESS, HUB_UPLOAD_FAIL,
  HUB_DELETE_FILE_SUCCESS,
  HUB_VIEW_URL_REQUEST, HUB_VIEW_URL_SUCCESS, HUB_VIEW_URL_FAIL,
  HUB_CLEAR_VIEW_URL,
  HUB_SET_NAVIGATION,
  HUB_RESET,
  HUB_SET_TENANT_CATEGORY,
  HUB_SHARED_PROPERTY_REQUEST, HUB_SHARED_PROPERTY_SUCCESS, HUB_SHARED_PROPERTY_FAIL,
  HUB_SHARED_COMPLIANCE_REQUEST, HUB_SHARED_COMPLIANCE_SUCCESS, HUB_SHARED_COMPLIANCE_FAIL,
} from "./hubTypes";

const initialState = {
  // Folder state
  folders: [],
  generalTotalCount: 0,
  foldersLoading: false,
  folderSaving: false,

  // File state
  files: [],
  filesLoading: false,
  uploading: false,

  // Navigation — what the user is currently viewing
  // rootFolder: 'docs' | 'account' | 'general' | null (landing)
  // folder: { _id, name, parentFolder } | null (at root level of rootFolder)
  // 'general' (labelled "Information" in the UI) is the default landing root.
  selectedRoot: "general",
  selectedFolder: null,

  // Tenant-only virtual categories under the General root:
  // 'general' | 'property' | 'compliance'. Kept separate from selectedFolder
  // (which addresses real HubFolder rows) so the LL/PL navigation semantics —
  // and the ?fileId= deep link that relies on them — are untouched.
  selectedTenantCategory: "general",

  // Shared content backing the 'property' and 'compliance' categories. Both are
  // grouped by property server-side: [{ propertyId, address, documents|records }]
  sharedPropertyGroups: [],
  sharedPropertyCount: 0,
  sharedPropertyLoading: false,
  sharedComplianceGroups: [],
  sharedComplianceCount: 0,
  sharedComplianceLoading: false,

  // File viewer
  viewUrl: null,
  viewDownloadUrl: null,
  viewFileType: null,
  viewFileName: null,
  viewUrlLoading: false,
};

const hubReducer = (state = initialState, action) => {
  switch (action.type) {
    case HUB_FOLDERS_REQUEST:
      return { ...state, foldersLoading: true };
    case HUB_FOLDERS_SUCCESS:
      return {
        ...state,
        foldersLoading: false,
        folders: action.payload.folders,
        generalTotalCount: action.payload.generalTotalCount ?? 0,
      };
    case HUB_FOLDERS_FAIL:
      return { ...state, foldersLoading: false };

    case HUB_CREATE_FOLDER_REQUEST:
      return { ...state, folderSaving: true };
    case HUB_CREATE_FOLDER_SUCCESS:
      return { ...state, folderSaving: false, folders: [...state.folders, action.payload] };
    case HUB_CREATE_FOLDER_FAIL:
      return { ...state, folderSaving: false };

    case HUB_RENAME_FOLDER_SUCCESS:
      return {
        ...state,
        folders: state.folders.map((f) => f._id === action.payload._id ? { ...f, name: action.payload.name } : f),
        selectedFolder: state.selectedFolder?._id === action.payload._id
          ? { ...state.selectedFolder, name: action.payload.name }
          : state.selectedFolder,
      };

    case HUB_DELETE_FOLDER_SUCCESS:
      return { ...state, folders: state.folders.filter((f) => f._id !== action.payload) };

    case HUB_FILES_REQUEST:
      return { ...state, filesLoading: true, files: [] };
    case HUB_FILES_SUCCESS:
      return { ...state, filesLoading: false, files: action.payload };
    case HUB_FILES_FAIL:
      return { ...state, filesLoading: false };

    case HUB_UPLOAD_REQUEST:
      return { ...state, uploading: true };
    case HUB_UPLOAD_SUCCESS: {
      const uploadedFolderId = action.payload?.folderId;
      const uploadedRoot = action.payload?.parentFolder;
      const updatedFolders = uploadedFolderId
        ? state.folders.map((f) =>
            f._id === uploadedFolderId ? { ...f, fileCount: (f.fileCount || 0) + 1 } : f
          )
        : state.folders;
      const updatedGeneralCount = uploadedRoot === "general"
        ? state.generalTotalCount + 1
        : state.generalTotalCount;
      return {
        ...state,
        uploading: false,
        files: [...state.files, action.payload],
        folders: updatedFolders,
        generalTotalCount: updatedGeneralCount,
      };
    }
    case HUB_UPLOAD_FAIL:
      return { ...state, uploading: false };

    case HUB_DELETE_FILE_SUCCESS: {
      const deletedFile = state.files.find((f) => f._id === action.payload);
      const foldersAfterDelete = deletedFile?.folderId
        ? state.folders.map((f) =>
            f._id === deletedFile.folderId ? { ...f, fileCount: Math.max(0, (f.fileCount || 0) - 1) } : f
          )
        : state.folders;
      const generalCountAfterDelete = deletedFile?.parentFolder === "general"
        ? Math.max(0, state.generalTotalCount - 1)
        : state.generalTotalCount;
      return {
        ...state,
        files: state.files.filter((f) => f._id !== action.payload),
        folders: foldersAfterDelete,
        generalTotalCount: generalCountAfterDelete,
      };
    }

    case HUB_VIEW_URL_REQUEST:
      return { ...state, viewUrlLoading: true, viewUrl: null };
    case HUB_VIEW_URL_SUCCESS:
      return {
        ...state,
        viewUrlLoading: false,
        viewUrl: action.payload.url,
        viewDownloadUrl: action.payload.downloadUrl,
        viewFileType: action.payload.fileType,
        viewFileName: action.payload.fileName,
      };
    case HUB_VIEW_URL_FAIL:
      return { ...state, viewUrlLoading: false };
    case HUB_CLEAR_VIEW_URL:
      return { ...state, viewUrl: null, viewDownloadUrl: null, viewFileType: null, viewFileName: null };

    case HUB_SET_NAVIGATION:
      return {
        ...state,
        selectedRoot: action.payload.rootFolder,
        selectedFolder: action.payload.folder,
        files: [],
        // Switching root card returns a tenant to the default category — and keeps
        // the ?fileId= deep link (which navigates to General) landing on it.
        selectedTenantCategory: "general",
      };

    case HUB_SET_TENANT_CATEGORY:
      return { ...state, selectedTenantCategory: action.payload };

    // Unlike HUB_FILES_REQUEST, these keep the previous groups while loading so
    // re-selecting a category doesn't flash an empty panel.
    case HUB_SHARED_PROPERTY_REQUEST:
      return { ...state, sharedPropertyLoading: true };
    case HUB_SHARED_PROPERTY_SUCCESS:
      return {
        ...state,
        sharedPropertyLoading: false,
        sharedPropertyGroups: action.payload.properties || [],
        sharedPropertyCount: action.payload.totalCount ?? 0,
      };
    case HUB_SHARED_PROPERTY_FAIL:
      return { ...state, sharedPropertyLoading: false };

    case HUB_SHARED_COMPLIANCE_REQUEST:
      return { ...state, sharedComplianceLoading: true };
    case HUB_SHARED_COMPLIANCE_SUCCESS:
      return {
        ...state,
        sharedComplianceLoading: false,
        sharedComplianceGroups: action.payload.properties || [],
        sharedComplianceCount: action.payload.totalCount ?? 0,
      };
    case HUB_SHARED_COMPLIANCE_FAIL:
      return { ...state, sharedComplianceLoading: false };

    case HUB_RESET:
      return initialState;

    default:
      return state;
  }
};

export default hubReducer;
